/**
 * F4 Health Overview page — auto-refresh 30s.
 * Shows 8 sections con traffic light. Endpoint: GET /api/admin/health-overview.
 */

import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { CheckCircle, AlertTriangle, AlertCircle, RefreshCw } from 'lucide-react';
import HealthTimeseriesChart from './HealthTimeseriesChart';

type Light = 'green' | 'yellow' | 'red';

interface SectionData<T = unknown> {
  status: Light;
  reason?: string;
  data: T;
}

interface HealthOverview {
  generated_at: string;
  overall: Light;
  sections: Record<string, SectionData>;
}

interface HealthSnapshot {
  id: number;
  captured_at: string;
  overall: Light;
  pm2_status?: Light;
  processes_running_status?: Light;
  queues_status?: Light;
  disk_status?: Light;
  errors_recent_status?: Light;
  reasons: Record<string, string | null>;
}

interface ApiEnvelope {
  success: boolean;
  data?: HealthOverview;
  elapsed_ms?: number;
  error?: string;
}

interface HistoryEnvelope {
  success: boolean;
  data?: HealthSnapshot[];
  count?: number;
  error?: string;
}

interface TopErrorRow {
  fingerprint: string;
  pattern: string;
  total_count: number;
  first_seen: string;
  last_seen: string;
  sample_msg: string | null;
  sample_module: string | null;
  recent_count: number;
  hourly_buckets: Record<string, number>;
}

interface TopErrorsEnvelope {
  success: boolean;
  data?: TopErrorRow[];
  count?: number;
  hours?: number;
  error?: string;
}

interface SloRow {
  id: string;
  description: string;
  target_pct: number;
  current_pct: number | null;
  light: Light;
  total: number;
  compliant: number;
  error_budget_remaining_pct: number | null;
  window: string;
}

interface SloEnvelope {
  success: boolean;
  data?: { overall: Light; slos: SloRow[] };
  hours?: number;
  error?: string;
}

interface DeadJobRow {
  id: number;
  type: string;
  attempts: number;
  max_attempts: number;
  error: string | null;
  created_at: string;
  completed_at: string;
}

interface DeadByTypeRow {
  type: string;
  dead_count: number;
  last_died: string;
}

interface DlqEnvelope {
  success: boolean;
  data?: {
    health: { dead_total: number; done_total: number; ratio_pct: number; window_hours: number };
    by_type: DeadByTypeRow[];
    list: DeadJobRow[];
  };
  error?: string;
}

const SECTION_LABELS: Record<string, string> = {
  pm2: 'PM2 Procesos',
  processes_running: 'Procesos DB Activos',
  queues: 'Queues',
  disk: 'Disco',
  pg_backup: 'Copia de seguridad de la base',
  errors_recent: 'Errores Recientes (5min)',
  amazon_keys: 'Llaves de Amazon (bloqueos y vencimiento del secreto)',
  ml_tokens: 'ML Tokens',
  gitnexus: 'GitNexus Index',
  mcp_servers: 'MCP Servers',
  item_sources_coverage: 'Cobertura PM (item_sources)',
  up_link_coverage: 'Cobertura PM UP (cbt_item_id)',
  ml_truth_desync: 'Desync ML (activo local / pausado ML)',
  amazon_cancelled_purchases: 'Amazon canceló la compra (sin reposición)',
  amazon_assistant: 'Asistente de Amazon (sesión)',
  assistant: 'Asistente de correo',
  ml_browser: 'Navegador de Mercado Libre (sesiones)',
};

function lightClasses(light: Light): string {
  switch (light) {
    case 'green':
      return 'bg-green-100 border-green-500 text-green-800';
    case 'yellow':
      return 'bg-yellow-100 border-yellow-500 text-yellow-800';
    case 'red':
      return 'bg-red-100 border-red-500 text-red-800';
  }
}

function LightIcon({ light }: { light: Light }) {
  if (light === 'green') return <CheckCircle className="w-5 h-5 text-green-600" />;
  if (light === 'yellow') return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
  return <AlertCircle className="w-5 h-5 text-red-600" />;
}

export default function HealthOverview() {
  const [overview, setOverview] = useState<HealthOverview | null>(null);
  const [history, setHistory] = useState<HealthSnapshot[]>([]);
  const [topErrors, setTopErrors] = useState<TopErrorRow[]>([]);
  const [slos, setSlos] = useState<SloRow[]>([]);
  const [dlq, setDlq] = useState<DlqEnvelope['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);

  const fetchOverview = async () => {
    try {
      const [oRes, hRes, eRes, sRes, dRes] = await Promise.all([
        api.get<HealthOverview>('/admin/health-overview') as Promise<ApiEnvelope>,
        api.get<HealthSnapshot[]>('/admin/health-history?hours=24&limit=100') as Promise<HistoryEnvelope>,
        api.get<TopErrorRow[]>('/admin/errors/top?hours=24&limit=10') as Promise<TopErrorsEnvelope>,
        api.get('/admin/slo?hours=168') as Promise<SloEnvelope>,
        api.get('/admin/jobs/dead?limit=50') as Promise<DlqEnvelope>,
      ]);
      if (oRes.success && oRes.data) {
        setOverview(oRes.data);
        setElapsedMs(oRes.elapsed_ms ?? null);
        setLastFetch(new Date());
        setError(null);
      } else {
        setError(oRes.error || 'Unknown error');
      }
      if (hRes.success && hRes.data) {
        setHistory(hRes.data);
      }
      if (eRes.success && eRes.data) {
        setTopErrors(eRes.data);
      }
      if (sRes.success && sRes.data) {
        setSlos(sRes.data.slos);
      }
      if (dRes.success && dRes.data) {
        setDlq(dRes.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    const id = setInterval(fetchOverview, 30000); // 30s
    return () => clearInterval(id);
  }, []);

  if (loading && !overview) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Cargando health overview...</span>
        </div>
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-300 rounded p-4 text-red-800">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!overview) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Health Overview</h1>
          <p className="text-sm text-gray-500">
            Auto-refresh cada 30s · last fetch: {lastFetch?.toLocaleTimeString()} · {elapsedMs}ms
          </p>
        </div>
        <button
          onClick={fetchOverview}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refrescar
        </button>
      </div>

      {/* Overall banner */}
      <div className={`border-l-4 rounded p-4 mb-6 ${lightClasses(overview.overall)}`}>
        <div className="flex items-center gap-3">
          <LightIcon light={overview.overall} />
          <div>
            <div className="font-semibold">Estado general: {overview.overall.toUpperCase()}</div>
            <div className="text-sm">generated_at: {overview.generated_at}</div>
          </div>
        </div>
      </div>

      {/* Sections grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(overview.sections).map(([key, section]) => (
          <div
            key={key}
            className={`border-l-4 rounded p-4 ${lightClasses(section.status)}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 font-semibold">
                <LightIcon light={section.status} />
                {SECTION_LABELS[key] || key}
              </div>
              <span className="text-xs uppercase font-mono">{section.status}</span>
            </div>
            {section.reason && (
              <div className="text-sm mb-2 italic">{section.reason}</div>
            )}
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                Ver detalle
              </summary>
              <pre className="mt-2 bg-white/50 p-2 rounded overflow-x-auto text-xs">
                {JSON.stringify(section.data, null, 2)}
              </pre>
            </details>
          </div>
        ))}
      </div>

      {/* Timeseries chart 24h */}
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">
            Timeseries últimas 24h ({history.length} snapshots)
          </h2>
          <HealthTimeseriesChart snapshots={history} />
        </div>
      )}

      {/* Tabla detalle 24h */}
      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">
            Detalle por snapshot (últimos 50 de {history.length})
          </h2>
          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Captured</th>
                  <th className="px-3 py-2 text-left">Overall</th>
                  <th className="px-3 py-2 text-left">Reasons (red/yellow only)</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 50).map((snap) => {
                  const reasonEntries = Object.entries(snap.reasons || {})
                    .filter(([, r]) => r)
                    .map(([k, r]) => `${k}: ${r}`);
                  return (
                    <tr key={snap.id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">
                        {new Date(snap.captured_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs uppercase font-semibold ${lightClasses(snap.overall)}`}>
                          {snap.overall}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        {reasonEntries.length === 0 ? <span className="text-gray-400">—</span> : reasonEntries.join(' · ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ola 1 Etapa 3b — SLOs (7 días) */}
      {slos.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">SLOs — últimos 7 días</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {slos.map((slo) => (
              <div
                key={slo.id}
                className={`border-l-4 rounded p-4 ${lightClasses(slo.light)}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-mono opacity-70">{slo.id}</div>
                    <div className="font-semibold">{slo.description}</div>
                  </div>
                  <LightIcon light={slo.light} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-xs opacity-70">Current</div>
                    <div className="font-bold text-lg">
                      {slo.current_pct !== null ? `${slo.current_pct.toFixed(2)}%` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs opacity-70">Target</div>
                    <div className="font-bold text-lg">{slo.target_pct}%</div>
                  </div>
                  <div>
                    <div className="text-xs opacity-70">Error budget</div>
                    <div className="font-bold text-lg">
                      {slo.error_budget_remaining_pct !== null
                        ? `${slo.error_budget_remaining_pct >= 0 ? '+' : ''}${slo.error_budget_remaining_pct.toFixed(2)}%`
                        : '—'}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs opacity-70">
                  {slo.compliant.toLocaleString()} / {slo.total.toLocaleString()} compliant · ventana {slo.window}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ola 2.3 — Dead Letter Queue */}
      {dlq && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">
            Dead Letter Queue —{' '}
            <span
              className={
                dlq.health.ratio_pct > 5
                  ? 'text-red-600'
                  : dlq.health.ratio_pct > 1
                    ? 'text-yellow-600'
                    : 'text-gray-600'
              }
            >
              {dlq.health.dead_total} dead · {dlq.health.ratio_pct.toFixed(2)}% del total ({dlq.health.window_hours}h)
            </span>
          </h2>
          {dlq.by_type.length > 0 && (
            <div className="bg-white border rounded overflow-hidden mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-right">Dead</th>
                    <th className="px-3 py-2 text-left">Última muerte</th>
                  </tr>
                </thead>
                <tbody>
                  {dlq.by_type.slice(0, 10).map((row) => (
                    <tr key={row.type} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{row.type}</td>
                      <td className="px-3 py-2 text-right font-semibold">{row.dead_count}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {new Date(row.last_died).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {dlq.list.length > 0 && (
            <div className="bg-white border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-right">Attempts</th>
                    <th className="px-3 py-2 text-left">Error (recortado)</th>
                    <th className="px-3 py-2 text-left">Murió</th>
                  </tr>
                </thead>
                <tbody>
                  {dlq.list.slice(0, 20).map((job) => (
                    <tr key={job.id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">#{job.id}</td>
                      <td className="px-3 py-2 font-mono text-xs">{job.type}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {job.attempts}/{job.max_attempts}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700 max-w-md truncate" title={job.error ?? ''}>
                        {job.error ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {new Date(job.completed_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Ola 1 Etapa 3a — Top 10 errores agrupados (últimas 24h) */}
      {topErrors.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold mb-3">
            Top errores agrupados — últimas 24h ({topErrors.length})
          </h2>
          <div className="bg-white border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">FP</th>
                  <th className="px-3 py-2 text-left">Módulo</th>
                  <th className="px-3 py-2 text-left">Patrón</th>
                  <th className="px-3 py-2 text-right">24h</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-left">Última vez</th>
                </tr>
              </thead>
              <tbody>
                {topErrors.map((row) => (
                  <tr key={row.fingerprint} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{row.fingerprint}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      {row.sample_module ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-800" title={row.sample_msg ?? ''}>
                      {row.pattern}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{row.recent_count}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{row.total_count}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {new Date(row.last_seen).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 text-xs text-gray-500">
        Endpoint: <code>GET /api/admin/health-overview</code> · history:{' '}
        <code>GET /api/admin/health-history?hours=24</code> · errors:{' '}
        <code>GET /api/admin/errors/top?hours=24</code> · auth: admin/api-key ·
        Spec: <code>docs/superpowers/specs/2026-05-09-f4-health-overview-design.md</code>
      </div>
    </div>
  );
}
